import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AnnualReportMetaInformationDTO, AnnualReportProvider } from '@modeso/aumico-lib-annualreport-fe';
import { AnnouncementsProvider, IPageHeader } from '@modeso/aumico-lib-core-fe';
import { IAnnualReport } from '@modeso/types__aumico-lib-annualreport-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject, Store } from '@ngrx/store';
import { filter, of } from 'rxjs';
import { delay, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { TasksOverviewActions } from '../../../../../aumico-lib-cpmm-fe/src/lib/actions/tasks.actions';
import { ProjectsOverviewActions, ProjectsOverviewState } from '../../../../../aumico-lib-projects-fe/src/lib';
import { OrganizationProvider } from '../../../../../aumico-lib-users-fe/src/public-api';
import { Paths } from '../../enums/paths.enum';
import { RouteLabels } from '../../enums/routeLabels.enum';
const COMPANY_YEARS_SEPARATOR = ' - ';
const YEARS_SEPARATOR = '/';
const LAST_TWO_DIGITS_OF_YEAR = -2;

@UntilDestroy()
@Component({
    selector: 'app-financial-statement-edit',
    template: `<app-aumico-layout>
        <ng-container header>
            <ham-page-header [title]="pageHeader.value" [backButtonRoute]="backButtonRoute"></ham-page-header>
            <ham-page-tab-navigation [navigationItems]="activeNavigationTabs"></ham-page-tab-navigation>
        </ng-container>
        <ng-container content>
            <router-outlet></router-outlet>
        </ng-container>
    </app-aumico-layout>`,
})
export class CpmmPage implements OnInit, OnDestroy {
    pageHeader: IPageHeader = {
        isLocalizedKey: true,
        value: RouteLabels.PROJECT_MAIN_NAVIGATION,
    };

    activeNavigationTabs: {
        routerLink: string;
        tabLabelKey: string;
    }[] = [
            {
                routerLink: Paths.MANDATE_MANAGEMENT,
                tabLabelKey: RouteLabels.REPORT_MANDATE_MANAGEMENT,
            },
            {
                routerLink: Paths.CLOSING_WORKFLOW,
                tabLabelKey: RouteLabels.REPORT_CLOSING_WORKFLOW,
            },
            {
                routerLink: Paths.REPORT,
                tabLabelKey: RouteLabels.REPORT,
            },
        ];

    backButtonRoute: string = '../../../project/overview';

    constructor(
        private readonly providerOfAnnualReports: AnnualReportProvider,
        private readonly organizationProvider: OrganizationProvider,
        private store: Store<ProjectsOverviewState>,
        private route: ActivatedRoute,
        private router: Router,
        private readonly actionsListener$: ActionsSubject,
        private readonly announcementsProvider: AnnouncementsProvider,
    ) { }

    ngOnDestroy(): void {
        this.store.dispatch(
            ProjectsOverviewActions.clearActiveProjectId(), // Clear the active project ID when leaving the page ,
        );
        this.store.dispatch(TasksOverviewActions.clearActiveTasks());
        this.providerOfAnnualReports.resetActiveAnnualReport(); // Reset the active annual report
    }

    private initForDirectNavigation(): void { }

    ngOnInit(): void {
        this.announcementsProvider.dispatchGetUserAnnouncements();

        this.route.queryParams.subscribe((params) => {
            if (params['backTarget']) {
                this.backButtonRoute = params['backTarget'];
            }
        });
        this.providerOfAnnualReports
            .getActiveAnnualReport$()
            .pipe(
                filter(
                    (activeAnnual: IAnnualReport) =>
                        (activeAnnual != null && activeAnnual.generalInformation != null) ||
                        (activeAnnual != null && activeAnnual.setupData != null),
                ),
                withLatestFrom(this.organizationProvider.getCurrentOrganization$()),
                untilDestroyed(this),
            )
            .subscribe(([annualReport]) => {
                this.handleChangePageHeaderForAnnualReport(annualReport);
            });

        this.providerOfAnnualReports
            .getActiveAnnualReportEncryptedId$()
            .pipe(
                switchMap((encryptedId: string) =>
                    this.providerOfAnnualReports.getMetaInformationByEncryptedId$(encryptedId),
                ),
                switchMap((metaInfo: AnnualReportMetaInformationDTO) =>
                    of(metaInfo).pipe(
                        filter((annualReportMetaInfo) => annualReportMetaInfo != null),
                        delay(1000), // ⏱️ wait 1 second for prject meta information record to be created
                        tap(() => {
                            this.store.dispatch(
                                ProjectsOverviewActions.fetchProjectIdByAnnualReportMetaInfoId({
                                    annualReportMetaId: metaInfo._id,
                                }),
                            );
                        }),
                    ),
                ),
                untilDestroyed(this),
            )
            .subscribe({});

        this.actionsListener$
            .pipe(ofType(ProjectsOverviewActions.projectIdByAnnualReportMetaInfoIdFetchFailure), untilDestroyed(this))
            .subscribe((error) => { });
    }

    private handleChangePageHeaderForAnnualReport(annualReport: IAnnualReport): void {
        const constructedAnnualReportPageHeader = this.constructAnnualReportPageHeader(annualReport);

        this.pageHeader = {
            isLocalizedKey: false,
            value: constructedAnnualReportPageHeader,
        };
    }

    private constructAnnualReportPageHeader(annualReport: IAnnualReport): string {
        if (annualReport.generalInformation != null) {

            const companyName = annualReport.generalInformation.company.label ? annualReport.generalInformation.company.name + ' (' + annualReport.generalInformation.company.label + ')' : annualReport.generalInformation.company.name;

            return (
                companyName +
                COMPANY_YEARS_SEPARATOR +
                this.getYearFromDate(
                    annualReport.generalInformation.balanceSheetDate,
                    annualReport.generalInformation.balancePreviousYearDate,
                )
            );
        } else if (annualReport.setupData != null) {

            const companyName = annualReport.setupData.company.label ? annualReport.setupData.company.name + ' (' + annualReport.setupData.company.label + ')' : annualReport.setupData.company.name;

            return (
                companyName +
                COMPANY_YEARS_SEPARATOR +
                this.getYearFromDate(annualReport.setupData.balanceSheetDate, undefined)
            );
        }
        return '';
    }

    private getYearFromDate(balanceSheetDate: string, balancePreviousYearDate: string | null): string {
        const balanceSheetCurrentYear = new Date(balanceSheetDate).getFullYear().toString();

        if (balancePreviousYearDate == null) {
            const balanceSheetDateObj = new Date(balanceSheetDate);
            balancePreviousYearDate = new Date(
                balanceSheetDateObj.setFullYear(balanceSheetDateObj.getFullYear() - 1),
            ).toISOString();
        }
        const balanceSheetPreviousYear = new Date(balancePreviousYearDate).getFullYear().toString();

        if (balanceSheetCurrentYear === balanceSheetPreviousYear) {
            return balanceSheetCurrentYear;
        }
        const balanceSheetCurrentYearShortened = balanceSheetCurrentYear.slice(LAST_TWO_DIGITS_OF_YEAR);
        const balanceSheetPreviousYearShortened = balanceSheetPreviousYear.slice(LAST_TWO_DIGITS_OF_YEAR);

        return balanceSheetCurrentYearShortened + YEARS_SEPARATOR + balanceSheetPreviousYearShortened;
    }

    goBack() {
        if (this.backButtonRoute) {
            this.router.navigateByUrl(decodeURIComponent(this.backButtonRoute));
        } else {
            // Fallback if backButtonRoute is not set
            this.router.navigate(['../../../project/overview']);
        }
    }
}
